import { z } from 'zod';

/** The standard route_type values (= 處理方式) operators can pick
 *  for a package. Mirrors backend `ALLOWED_ROUTES`.
 *
 *  Routing says only THAT something is dismantled by hand, never how: the
 *  light/heavy split was the platform imposing its own two methods on every
 *  processor. The method used is chosen per session from the company's own
 *  list (migration 0032). */
export const routeTypeValues = [
  'manual_dismantling',
  'material_sales',
  'storage_inventory',
] as const;
export type RouteType = (typeof routeTypeValues)[number];

/** Legacy read-only values still returned on rows written before 0032. */
export const sortingPackageRouteTypeValues = [
  ...routeTypeValues,
  'manual_light_dismantling',
  'manual_heavy_dismantling',
  'secondary_process',
] as const;

export const sortingProcessingStateValues = [
  'available',
  'dismantling_in_progress',
  'dismantled',
  'outbound_draft',
  'dispatched',
  'locked',
] as const;
export type SortingProcessingState = (typeof sortingProcessingStateValues)[number];

export const sortingAllowedActionValues = [
  'route',
  'change_warehouse',
  /** Already routed for dismantling — the 解体 page can take it as-is. */
  'start_dismantling',
  /** Dismantling-eligible but routed elsewhere — route it first, then hand off. */
  'route_and_dismantle',
  'continue_dismantling',
  'view_history',
] as const;
export type SortingAllowedAction = (typeof sortingAllowedActionValues)[number];

/** One row from `GET /api/sorting-packages`. */
export const sortingPackageSchema = z.object({
  inbound_package_id: z.string(),
  package_code: z.string(),
  incoming_item_id: z.string(),
  incoming_item_name: z.string(),
  warehouse_id: z.string().nullable().optional(),
  warehouse_code: z.string().nullable().optional(),
  warehouse_display_name: z.string().nullable().optional(),
  inbound_date: z.string(),
  lot_code: z.string(),
  route_type: z.enum(sortingPackageRouteTypeValues).nullable().optional(),
  status: z.string(),
  processing_state: z.enum(sortingProcessingStateValues).optional(),
  processing_reference_type: z.string().nullable().optional(),
  processing_reference_id: z.string().nullable().optional(),
  allowed_actions: z.array(z.enum(sortingAllowedActionValues)).optional(),
  dismantling_progress_percent: z.number().min(0).nullable().optional(),
  dismantling_input_weight_kg: z.string().nullable().optional(),
  dismantling_output_weight_kg: z.string().nullable().optional(),
});

export type SortingPackageDto = z.infer<typeof sortingPackageSchema>;

/** Body for `POST /api/sorting-packages/bulk-route`. */
export const bulkRouteSchema = z.object({
  package_ids: z.array(z.string().min(1)).min(1),
  route_type: z.enum(routeTypeValues),
  change_reason: z.string().optional(),
});

export type BulkRouteInput = z.infer<typeof bulkRouteSchema>;

/** Body for `POST /api/sorting-packages/bulk-warehouse`. */
export const bulkSortingWarehouseSchema = z.object({
  package_ids: z.array(z.string().min(1)).min(1),
  warehouse_id: z.string().nullable().optional(),
  change_reason: z.string().optional(),
});

export type BulkSortingWarehouseInput = z.infer<typeof bulkSortingWarehouseSchema>;

/** Query parameters for `GET /api/sorting-packages`. */
export interface SortingPackageFilters {
  batch_id?: string;
  route_type?: RouteType;
  warehouse_id?: string;
  processing_state?: SortingProcessingState | 'all';
  page?: number;
  per_page?: number;
}

export const getSortingProcessingState = (pkg: SortingPackageDto): SortingProcessingState =>
  pkg.processing_state ?? 'available';

export const getSortingAllowedActions = (pkg: SortingPackageDto): SortingAllowedAction[] =>
  pkg.allowed_actions ??
  (getSortingProcessingState(pkg) === 'available'
    ? ['route', 'change_warehouse', 'start_dismantling']
    : []);

// ---------------------------------------------------------------------------
// Legacy sorting-decision DTO retained for backwards compatibility with
// other callers (test fixture, SortingPage that wasn't rebuilt). Kept here
// so this module keeps exporting the same shape via index.ts.
// ---------------------------------------------------------------------------

export const sortingDecisionSchema = z.object({
  sorting_decision_id: z.string(),
  inbound_lot_id: z.string(),
  lot_code: z.string().optional(),
  inbound_date: z.string().optional(),
  route_type: z.string(),
  decision_reason: z.string().nullable().optional(),
  decided_by: z.string().nullable().optional(),
  decided_at: z.string().nullable().optional(),
  confirmed_by: z.string().nullable().optional(),
  confirmed_at: z.string().nullable().optional(),
});

export type SortingDecisionDto = z.infer<typeof sortingDecisionSchema>;

export const sortingDecisionCreateSchema = z.object({
  route_type: z.string(),
  decision_reason: z.string().optional(),
});

export type SortingDecisionCreateInput = z.infer<typeof sortingDecisionCreateSchema>;
